import React, { useState, useEffect } from 'react';
import './ClientComponentsSidebar.css';

const ClientComponentsSidebar = ({ 
  isOpen, 
  onClose, 
  elements,
  setElements,
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames,
  setSelectedElement,
  setNewlyAddedElement,
  showNotification,
  setShowClientComponentsSidebar
}) => {
  const [clientComponents, setClientComponents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Function to dynamically import all components from Client_Components folder
  useEffect(() => {
    const loadClientComponents = async () => {
      try {
        setLoading(true);
        
        // Load components using the new registry system
        const loadedComponents = [];
        
        try {
          // Import the main index file which contains all available components
          const { getAvailableComponents } = await import('./indexClientComp.js');
          
          // Get all available components
          const availableComponents = getAvailableComponents();
          loadedComponents.push(...availableComponents);
          
        } catch (indexError) {
          console.warn('Failed to load components from registry:', indexError);
          
          // Fallback to manual discovery if needed
          const componentNames = [
            // 'Button', 'PhotographyNavbar'
            // Add more component names here as fallback
          ];
          
          for (const componentName of componentNames) {
            try {
              const module = await import(`./Components/${componentName}.js`);
              const createFunctionName = `create${componentName}Elements`;
              
              if (module[createFunctionName] && typeof module[createFunctionName] === 'function') {
                loadedComponents.push({
                  id: componentName.toLowerCase(),
                  name: componentName,
                  createFunction: module[createFunctionName],
                  thumbnail: `https://via.placeholder.com/250x120?text=${componentName}+Component`,
                  description: `Custom ${componentName} component`,
                  category: 'General',
                  tags: []
                });
              }
            } catch (error) {
              continue;
            }
          }
        }

        setClientComponents(loadedComponents);
        
      } catch (error) {
        console.error('Error loading client components:', error);
        showNotification('Failed to load client components', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadClientComponents();
    }
  }, [isOpen, showNotification]);

  const handleComponentSelect = (component) => {
    if (!component || !component.createFunction) {
      showNotification("Invalid component", "error");
      return;
    }

    try {
      const getHierarchyName = (elementType) => {
        const elementCount = elements.filter(el => el.type === elementType).length + 1;
        return `${elementType.charAt(0).toUpperCase() + elementType.slice(1)} ${elementCount}`;
      };

      // Call the component's create function
      const { elements: componentElements } = component.createFunction(
        getHierarchyName,
        currentPageId,
        setClassStylesRegistry,
        setGlobalClassNames
      );
      
      // Add the elements to the page
      setElements(prevElements => [...prevElements, ...componentElements]);

      // Select the first element (usually the main container)
      if (componentElements.length > 0) {
        setSelectedElement(componentElements[0]);
        setNewlyAddedElement(componentElements[0]);
      }
      
      showNotification(`Added ${component.name} component to the page`, "success");
      
      // Close the sidebar
      setShowClientComponentsSidebar(false);
    } catch (error) {
      console.error('Error adding component:', error);
      showNotification(`Failed to add ${component.name} component`, "error");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sidebar client-components-sidebar">
      <div className="sidebar-header">
        <h3>Client Components</h3>
        <button className="close-btn" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>
      
      <div className="sidebar-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading components...</p>
          </div>
        ) : clientComponents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <i className="fas fa-puzzle-piece"></i>
            </div>
            <h4>No Components Found</h4>
            <p>Add component files to the Client_Components folder to see them here.</p>
          </div>
        ) : (
          <div className="component-items">
            {clientComponents.map(component => (
              <div 
                key={component.id}
                className="component-item"
                onClick={() => handleComponentSelect(component)}
              >
                <div className="component-preview">
                  <img src={component.thumbnail} alt={component.name} />
                </div>
                <div className="component-info">
                  <h4>{component.name}</h4>
                  <p>{component.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientComponentsSidebar; 